// src/pages/Admin/AdminDashboardPage_Enhanced.jsx
import React, { useEffect, useState, useCallback  } from "react";
import { adminService } from "../../services/admin.service";
import "./admin.css";
import { Bar, Doughnut } from "react-chartjs-2";
import { jsPDF } from "jspdf";
import * as XLSX from "xlsx";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
} from "chart.js";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
);

const AdminDashboardPage = () => {
  const [overview, setOverview] = useState(null);
  const [revenueByDay, setRevenueByDay] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dateRange, setDateRange] = useState("30");
  const [alerts, setAlerts] = useState([]);
  const [todayStats, setTodayStats] = useState(null);
  const chartContainerRef = React.useRef(null);

  const loadData = useCallback(async (days = 30) => {
    try {
      setLoading(true);
      setError("");

      const end = new Date();
      const start = new Date("2025-01-01");

      const from = start.toISOString().split("T")[0];
      const to = end.toISOString().split("T")[0];

      const [
        overviewRes,
        revenueRes,
        topProductsRes,
        topCustomersRes,
        ordersRes,
      ] = await Promise.all([
        adminService.getOverview({ from, to }),
        adminService.getRevenueByDay({ from, to }),
        adminService.getTopProducts({ limit: 5, from, to }),
        adminService.getTopCustomers({ limit: 5, from, to }),
        adminService.getOrders(),
      ]);

      setOverview(overviewRes || {});
      setRevenueByDay(revenueRes?.days || []);
      setTopProducts(topProductsRes?.items || []);
      setTopCustomers(topCustomersRes?.customers || []);
      setAllOrders(ordersRes?.data || ordersRes || []);

      // Generate alerts
      generateAlerts(overviewRes || {}, ordersRes || []);

      // Calculate today's stats
      calculateTodayStats(revenueRes?.days || [], ordersRes || []);
    } catch (err) {
      console.error("Dashboard error:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load dashboard data",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const generateAlerts = (overview, orders) => {
    const newAlerts = [];

    // Check for pending orders
    if (overview?.orders?.pendingOrders > 5) {
      newAlerts.push({
        type: "warning",
        icon: "⏳",
        message: `${overview.orders.pendingOrders} orders pending payment`,
      });
    }

    // Check for cancelled orders
    if (overview?.orders?.cancelledOrders > 3) {
      newAlerts.push({
        type: "error",
        icon: "❌",
        message: `${overview.orders.cancelledOrders} orders cancelled recently`,
      });
    }

    // Check low average order value
    if (overview?.revenue?.averageOrderValue < 5000) {
      newAlerts.push({
        type: "info",
        icon: "📉",
        message: `Low average order value: ₹${overview.revenue.averageOrderValue}`,
      });
    }

    setAlerts(newAlerts);
  };

  const calculateTodayStats = (revenueByDay, orders) => {
    const today = new Date().toISOString().split("T")[0];
    const todayData = revenueByDay.find((d) => d.date === today);
    const todayOrders = orders.filter((o) => {
      const orderDate = new Date(o.createdAt).toISOString().split("T")[0];
      return orderDate === today;
    });

    setTodayStats({
      revenue: todayData?.revenue || 0,
      orders: todayData?.orders || 0,
      newOrders: todayOrders.length,
    });
  };

  useEffect(() => {
    loadData(parseInt(dateRange));
  }, [dateRange,loadData]);

  const handleDateRangeChange = (days) => {
    setDateRange(days.toString());
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 10;

    // Header
    doc.setFontSize(16);
    doc.text("Dashboard Report", 10, yPosition);
    yPosition += 10;

    // Date
    doc.setFontSize(10);
    doc.text(
      `Generated on: ${new Date().toLocaleDateString("en-IN")}`,
      10,
      yPosition,
    );
    yPosition += 10;

    // Key Metrics
    doc.setFontSize(12);
    doc.text("Key Metrics:", 10, yPosition);
    yPosition += 6;

    const revenue = overview?.revenue || {};
    const orders = overview?.orders || {};

    doc.setFontSize(10);
    doc.text(
      `Total Revenue: ₹${(revenue.totalRevenue || 0).toLocaleString("en-IN")}`,
      10,
      yPosition,
    );
    yPosition += 6;
    doc.text(`Total Orders: ${orders.totalOrders || 0}`, 10, yPosition);
    yPosition += 6;
    doc.text(`Paid Orders: ${orders.paidOrders || 0}`, 10, yPosition);
    yPosition += 6;
    doc.text(
      `Avg Order Value: ₹${(revenue.averageOrderValue || 0).toLocaleString("en-IN")}`,
      10,
      yPosition,
    );
    yPosition += 10;

    // Top Products
    doc.setFontSize(12);
    doc.text("Top 5 Products:", 10, yPosition);
    yPosition += 6;

    doc.setFontSize(9);
    topProducts.slice(0, 5).forEach((p, idx) => {
      if (yPosition > pageHeight - 10) {
        doc.addPage();
        yPosition = 10;
      }
      doc.text(
        `${idx + 1}. ${p.name || p.product_id} - ₹${(p.totalRevenue || 0).toLocaleString("en-IN")}`,
        10,
        yPosition,
      );
      yPosition += 5;
    });

    doc.save(`dashboard-report-${new Date().toISOString().split("T")[0]}.pdf`);
  };

  // Export to Excel
  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // Overview sheet
    const overviewData = [
      ["Dashboard Summary"],
      ["Date", new Date().toLocaleDateString("en-IN")],
      [],
      ["Total Revenue", overview?.revenue?.totalRevenue || 0],
      ["Total Orders", overview?.orders?.totalOrders || 0],
      ["Paid Orders", overview?.orders?.paidOrders || 0],
      ["Pending Orders", overview?.orders?.pendingOrders || 0],
      ["Avg Order Value", overview?.revenue?.averageOrderValue || 0],
    ];

    const ws1 = XLSX.utils.aoa_to_sheet(overviewData);
    XLSX.utils.book_append_sheet(wb, ws1, "Overview");

    // Top Products sheet
    if (topProducts.length > 0) {
      const productsData = [
        ["Product", "SKU", "Quantity Sold", "Total Revenue"],
        ...topProducts.map((p) => [
          p.name || p.product_id,
          p.sku || "-",
          p.totalQuantity || 0,
          p.totalRevenue || 0,
        ]),
      ];

      const ws2 = XLSX.utils.aoa_to_sheet(productsData);
      XLSX.utils.book_append_sheet(wb, ws2, "Top Products");
    }

    // Top Customers sheet
    if (topCustomers.length > 0) {
      const customersData = [
        ["Customer Name", "Email", "Total Spent", "Orders"],
        ...topCustomers.map((c) => [
          c.name || "N/A",
          c.email || "-",
          c.totalSpent || 0,
          c.orderCount || 0,
        ]),
      ];

      const ws3 = XLSX.utils.aoa_to_sheet(customersData);
      XLSX.utils.book_append_sheet(wb, ws3, "Top Customers");
    }

    XLSX.writeFile(
      wb,
      `dashboard-report-${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };

  if (loading) {
    return (
      <div className="admin-loading-container">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-error-alert">
        <strong>⚠️ Error:</strong> {error}
      </div>
    );
  }

  const revenue = overview?.revenue || {};
  const orders = overview?.orders || {};
  const customers = overview?.customers || {};

  const totalRevenue = Number(revenue.totalRevenue || 0);
  const averageOrderValue = Number(revenue.averageOrderValue || 0);
  const totalOrders = Number(orders.totalOrders || 0);
  const paidOrders = Number(orders.paidOrders || 0);
  const pendingOrders = Number(orders.pendingOrders || 0);
  const cancelledOrders = Number(orders.cancelledOrders || 0);
  const totalCustomers = Number(customers.totalCustomers || 0);
  const shippedOrders =
    totalOrders - paidOrders - pendingOrders - cancelledOrders;

  // const maxRevenue = Math.max(
  //   ...revenueByDay.map((d) => Number(d.revenue) || 0),
  //   1,
  // );

  return (
    <div className="admin-dashboard-page">
      {/* ===== ALERTS SECTION ===== */}
      {alerts.length > 0 && (
        <div className="alerts-section">
          <h3 style={{ margin: "0 0 12px 0" }}>⚠️ Alerts & Notifications</h3>
          <div className="alerts-container">
            {alerts.map((alert, idx) => (
              <div key={idx} className={`alert alert-${alert.type}`}>
                <span className="alert-icon">{alert.icon}</span>
                <span className="alert-message">{alert.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== TODAY'S METRICS ===== */}
      {todayStats && (
        <div className="todays-metrics-section">
          <h3 style={{ margin: "0 0 12px 0" }}>📅 Today's Performance</h3>
          <div className="todays-metrics-grid">
            <div className="today-card">
              <div className="today-label">💰 Today's Revenue</div>
              <div className="today-value">
                ₹{Number(todayStats.revenue || 0).toLocaleString("en-IN")}
              </div>
            </div>
            <div className="today-card">
              <div className="today-label">📦 Today's Orders</div>
              <div className="today-value">{todayStats.orders || 0}</div>
            </div>
            <div className="today-card">
              <div className="today-label">🆕 New Orders</div>
              <div className="today-value">{todayStats.newOrders || 0}</div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Top hero header ===== */}
      <div className="admin-dashboard-header">
        <div>
          <h2 className="admin-dashboard-title">Dashboard 📊</h2>
          <p className="admin-dashboard-subtitle">
            Real-time analytics and performance metrics for your store.
          </p>
        </div>

        <div className="admin-dashboard-actions">
          <div className="date-range-buttons">
            <button
              className={`date-btn ${dateRange === "7" ? "active" : ""}`}
              onClick={() => handleDateRangeChange(7)}
            >
              7 Days
            </button>
            <button
              className={`date-btn ${dateRange === "30" ? "active" : ""}`}
              onClick={() => handleDateRangeChange(30)}
            >
              30 Days
            </button>
            <button
              className={`date-btn ${dateRange === "90" ? "active" : ""}`}
              onClick={() => handleDateRangeChange(90)}
            >
              90 Days
            </button>
          </div>
          <button
            className="admin-refresh-btn"
            onClick={() => loadData(parseInt(dateRange))}
            title="Refresh data"
          >
            🔄 Refresh
          </button>
          <button
            className="export-btn export-pdf-btn"
            onClick={exportToPDF}
            title="Export as PDF"
          >
            📄 PDF
          </button>
          <button
            className="export-btn export-excel-btn"
            onClick={exportToExcel}
            title="Export as Excel"
          >
            📊 Excel
          </button>
        </div>
      </div>

      {/* ===== Metric cards ===== */}
      <div className="admin-dashboard-metrics">
        <div className="metric-card metric-card--purple">
          <div className="metric-label">💰 Total Revenue</div>
          <div className="metric-value">
            ₹
            {totalRevenue.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
          </div>
          <div className="metric-caption">Based on paid orders</div>
        </div>

        <div className="metric-card metric-card--blue">
          <div className="metric-label">📈 Avg Order Value</div>
          <div className="metric-value">
            ₹
            {averageOrderValue.toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            })}
          </div>
          <div className="metric-caption">{totalOrders} total orders</div>
        </div>

        <div className="metric-card metric-card--green">
          <div className="metric-label">✅ Orders</div>
          <div className="metric-value">
            {paidOrders} / {totalOrders}
          </div>
          <div className="metric-caption">Paid / Total in period</div>
        </div>

        <div className="metric-card metric-card--orange">
          <div className="metric-label">👥 Customers</div>
          <div className="metric-value">{totalCustomers}</div>
          <div className="metric-caption">
            {customers.newCustomersInRange || 0} new in this period
          </div>
        </div>
      </div>

      {/* ===== Main content 2-column layout ===== */}
      <div className="admin-dashboard-main">
        <div className="admin-dashboard-col">
          {/* Revenue Chart */}
          <div className="admin-card admin-dashboard-card">
            <div className="admin-dashboard-card-header">
              <div>
                <h3>Revenue Trend</h3>
                <p className="admin-dashboard-card-subtitle">
                  Daily revenue across the selected period
                </p>
              </div>
              {revenueByDay.length > 0 && (
                <div style={{ fontSize: "12px", color: "#666" }}>
                  {revenueByDay.length} days of data
                </div>
              )}
            </div>

            {revenueByDay.length > 0 ? (
              <div
                ref={chartContainerRef}
                style={{
                  position: "relative",
                  height: "400px",
                  overflowX: "auto",
                  overflowY: "hidden",
                  borderRadius: "6px",
                  backgroundColor: "#fafafa",
                  padding: "12px",
                }}
              >
                <div
                  style={{
                    position: "relative",
                    height: "380px",
                    width:
                      revenueByDay.length > 15
                        ? `${revenueByDay.length * 60}px`
                        : "100%",
                    minWidth: "100%",
                  }}
                >
                  <Bar
                    data={{
                      labels: revenueByDay.map((day) => {
                        if (day.date) {
                          const dateObj = new Date(day.date + "T00:00:00");
                          return dateObj.toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                          });
                        }
                        return day.date;
                      }),
                      datasets: [
                        {
                          label: "Daily Revenue",
                          data: revenueByDay.map(
                            (day) => Number(day.revenue) || 0,
                          ),
                          backgroundColor: "rgba(99, 102, 241, 0.7)",
                          borderColor: "rgba(99, 102, 241, 1)",
                          borderWidth: 2,
                          borderRadius: 6,
                          hoverBackgroundColor: "rgba(99, 102, 241, 0.9)",
                          hoverBorderColor: "rgba(99, 102, 241, 1)",
                          borderSkipped: false,
                        },
                      ],
                    }}
                    options={{
                      indexAxis: "x",
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: true,
                          position: "top",
                          labels: {
                            font: { size: 12, weight: "500" },
                            color: "#333",
                            padding: 15,
                          },
                        },
                        tooltip: {
                          backgroundColor: "rgba(0, 0, 0, 0.8)",
                          padding: 12,
                          titleFont: { size: 13, weight: "bold" },
                          bodyFont: { size: 12 },
                          borderColor: "rgba(99, 102, 241, 1)",
                          borderWidth: 2,
                          displayColors: false,
                          callbacks: {
                            title: function (context) {
                              return context[0].label;
                            },
                            label: function (context) {
                              const value = context.parsed.y || 0;
                              return `Revenue: ₹${value.toLocaleString(
                                "en-IN",
                                { maximumFractionDigits: 0 },
                              )}`;
                            },
                            afterLabel: function (context) {
                              const day = revenueByDay[context.dataIndex];
                              const orders = day.orders || 0;
                              return `Orders: ${orders}`;
                            },
                          },
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          grid: {
                            color: "rgba(0, 0, 0, 0.05)",
                            drawBorder: false,
                          },
                          ticks: {
                            font: { size: 11 },
                            color: "#666",
                            callback: function (value) {
                              return "₹" + value.toLocaleString("en-IN");
                            },
                          },
                        },
                        x: {
                          grid: { display: false },
                          ticks: {
                            font: { size: 10 },
                            color: "#666",
                            maxRotation: 45,
                            minRotation: 45,
                          },
                        },
                      },
                    }}
                  />
                </div>

                {revenueByDay.length > 15 && (
                  <div
                    style={{
                      textAlign: "center",
                      fontSize: "11px",
                      color: "#999",
                      marginTop: "8px",
                      padding: "0 12px",
                    }}
                  >
                    📱 Scroll horizontally to see more data →
                  </div>
                )}
              </div>
            ) : (
              <p className="no-data-message">No revenue data available</p>
            )}
          </div>

          {/* Order Status Chart */}
          <div className="admin-card admin-dashboard-card">
            <h3 style={{ marginTop: 0, marginBottom: "1rem" }}>
              📊 Order Status Breakdown
            </h3>
            {totalOrders > 0 ? (
              <div style={{ position: "relative", height: "300px" }}>
                <Doughnut
                  data={{
                    labels: [
                      "Paid ✅",
                      "Pending ⏳",
                      "Shipped 🚚",
                      "Cancelled ❌",
                    ],
                    datasets: [
                      {
                        data: [
                          paidOrders,
                          pendingOrders,
                          shippedOrders,
                          cancelledOrders,
                        ],
                        backgroundColor: [
                          "rgba(34, 197, 94, 0.8)",
                          "rgba(248, 113, 113, 0.8)",
                          "rgba(59, 130, 246, 0.8)",
                          "rgba(241, 65, 108, 0.8)",
                        ],
                        borderColor: [
                          "rgba(34, 197, 94, 1)",
                          "rgba(248, 113, 113, 1)",
                          "rgba(59, 130, 246, 1)",
                          "rgba(241, 65, 108, 1)",
                        ],
                        borderWidth: 2,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "bottom",
                        labels: {
                          font: { size: 11 },
                          padding: 12,
                        },
                      },
                      tooltip: {
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                        padding: 12,
                        callbacks: {
                          label: function (context) {
                            const label = context.label || "";
                            const value = context.parsed || 0;
                            const total = context.dataset.data.reduce(
                              (a, b) => a + b,
                              0,
                            );
                            const percentage = ((value / total) * 100).toFixed(
                              1,
                            );
                            return `${label}: ${value} (${percentage}%)`;
                          },
                        },
                      },
                    },
                  }}
                />
              </div>
            ) : (
              <p className="no-data-message">No order data available</p>
            )}
          </div>

          {/* Top Products */}
          <div className="admin-card admin-dashboard-card">
            <h3 style={{ marginTop: 0, marginBottom: "1rem" }}>
              🏆 Top Products (by Revenue)
            </h3>
            {topProducts.length > 0 ? (
              <table className="admin-table compact">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>SKU</th>
                    <th>Qty Sold</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {topProducts.map((p, idx) => (
                    <tr key={p.product_id || idx}>
                      <td>
                        <strong>{p.name || p.product_id}</strong>
                      </td>
                      <td className="sku-cell">{p.sku || "-"}</td>
                      <td className="qty-cell">{p.totalQuantity ?? 0}</td>
                      <td className="revenue-cell">
                        ₹
                        {Number(p.totalRevenue || 0).toLocaleString("en-IN", {
                          maximumFractionDigits: 0,
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="no-data-message">No product sales data available</p>
            )}
          </div>

          {/* Activity Feed */}
          <div className="admin-card admin-dashboard-card">
            <h3 style={{ marginTop: 0, marginBottom: "1rem" }}>
              🕐 Recent Activity Feed
            </h3>
            {allOrders && allOrders.length > 0 ? (
              <div className="activity-feed">
                {allOrders.slice(0, 10).map((order, idx) => {
                  const orderDate = new Date(order.createdAt);
                  const timeAgo = getTimeAgo(orderDate);
                  return (
                    <div key={idx} className="activity-item">
                      <div className="activity-icon">📦</div>
                      <div className="activity-content">
                        <div className="activity-title">
                          Order #{order.order_id} - {order.status || "Unknown"}
                        </div>
                        <div className="activity-meta">
                          ₹{(order.total_amount || 0).toLocaleString("en-IN")} •{" "}
                          {timeAgo}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="no-data-message">No recent activity</p>
            )}
          </div>
        </div>

        <div className="admin-dashboard-col admin-dashboard-col--side">
          {/* Quick Snapshot */}
          <div className="admin-card admin-dashboard-card">
            <h3 style={{ marginTop: 0 }}>📋 Quick Snapshot</h3>
            <ul className="dashboard-list">
              <li>
                <span>Pending Orders</span>
                <span className="badge badge-warning">{pendingOrders}</span>
              </li>
              <li>
                <span>Cancelled Orders</span>
                <span className="badge badge-danger">{cancelledOrders}</span>
              </li>
              <li>
                <span>Shipped Orders</span>
                <span className="badge badge-info">{shippedOrders}</span>
              </li>
              <li>
                <span>Total Customers</span>
                <span className="badge badge-success">{totalCustomers}</span>
              </li>
            </ul>
          </div>

          {/* Top Customers */}
          <div className="admin-card admin-dashboard-card">
            <h3 style={{ marginTop: 0, marginBottom: "1rem" }}>
              ⭐ Top Customers
            </h3>
            {topCustomers.length > 0 ? (
              <ul className="customers-list">
                {topCustomers.map((customer, idx) => (
                  <li key={customer.user_id || idx} className="customer-item">
                    <div className="customer-info">
                      <div className="customer-name">
                        {customer.name || customer.email}
                      </div>
                      <div className="customer-email">{customer.email}</div>
                    </div>
                    <div className="customer-stats">
                      <div className="stat">
                        <span className="label">Spent:</span>
                        <span className="value">
                          ₹
                          {Number(customer.totalSpent || 0).toLocaleString(
                            "en-IN",
                            { maximumFractionDigits: 0 },
                          )}
                        </span>
                      </div>
                      <div className="stat">
                        <span className="label">Orders:</span>
                        <span className="value">{customer.orderCount}</span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="no-data-message">No customer data available</p>
            )}
          </div>

          {/* Performance Summary */}
          <div className="admin-card admin-dashboard-card">
            <h3 style={{ marginTop: 0 }}>📈 Performance Summary</h3>
            <div className="performance-metrics">
              <div className="perf-metric">
                <div className="perf-label">Conversion Rate</div>
                <div className="perf-value">
                  {totalOrders > 0
                    ? ((paidOrders / totalOrders) * 100).toFixed(1)
                    : 0}
                  %
                </div>
              </div>
              <div className="perf-metric">
                <div className="perf-label">Avg Revenue/Day</div>
                <div className="perf-value">
                  ₹
                  {revenueByDay.length > 0
                    ? (
                        totalRevenue /
                        (revenueByDay.length === 0 ? 1 : revenueByDay.length)
                      ).toLocaleString("en-IN", {
                        maximumFractionDigits: 0,
                      })
                    : 0}
                </div>
              </div>
              <div className="perf-metric">
                <div className="perf-label">Repeat Customers</div>
                <div className="perf-value">
                  {totalOrders > 0
                    ? (
                        ((totalOrders - customers.distinctCustomersInRange) /
                          totalOrders) *
                        100
                      ).toFixed(1)
                    : 0}
                  %
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to calculate time ago
function getTimeAgo(date) {
  const seconds = Math.floor((new Date() - date) / 1000);
  let interval = seconds / 31536000;

  if (interval > 1) return Math.floor(interval) + " years ago";
  interval = seconds / 2592000;
  if (interval > 1) return Math.floor(interval) + " months ago";
  interval = seconds / 86400;
  if (interval > 1) return Math.floor(interval) + " days ago";
  interval = seconds / 3600;
  if (interval > 1) return Math.floor(interval) + " hours ago";
  interval = seconds / 60;
  if (interval > 1) return Math.floor(interval) + " minutes ago";
  return Math.floor(seconds) + " seconds ago";
}

export default AdminDashboardPage;
